import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table
} from "reactstrap";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { GetStores, GetDeliverySettings, CalculateDeliveryPlayground } from "../../../requests";
import { toast } from "react-toastify";

const extractData = (response) => {
  if (!response) return null;
  if (response.data !== undefined) {
    if (response.data && response.data.success && response.data.data !== undefined) {
      return response.data.data;
    }
    return response.data;
  }
  if (response.success && response.data !== undefined) {
    return response.data;
  }
  return response;
};

function isStoreOpen(openingTime, closingTime) {
  if (!openingTime || !closingTime) return true;
  try {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    const [opH, opM] = openingTime.split(':').map(Number);
    const [clH, clM] = closingTime.split(':').map(Number);
    
    const openMin = opH * 60 + opM;
    const closeMin = clH * 60 + clM;
    
    if (closeMin > openMin) {
      return currentTime >= openMin && currentTime <= closeMin;
    } else {
      // Overnight operating hours
      return currentTime >= openMin || currentTime <= closeMin;
    }
  } catch (e) {
    return true;
  }
}

export default function DeliveryPlayground() {
  const role = useSelector((state) => state?.login?.role);

  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [loadingStores, setLoadingStores] = useState(false);
  
  const [subtotal, setSubtotal] = useState("250");
  const [distance, setDistance] = useState("2.4");
  const [deliveryType, setDeliveryType] = useState("DELIVERY");

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Fetch store list on mount
  useEffect(() => {
    const fetchStores = async () => {
      try {
        setLoadingStores(true);
        const resp = await GetStores();
        const storeList = extractData(resp) || [];
        setStores(storeList);
        if (storeList.length > 0) {
          setSelectedStoreId(storeList[0].id || storeList[0]._id);
        }
      } catch (err) {
        console.error("Failed to fetch stores:", err);
        toast.error("Failed to load store outlets.");
      } finally {
        setLoadingStores(false);
      }
    };
    fetchStores();
  }, []);

  // Wait for role to be hydrated from Redux before redirecting
  if (role === null || role === undefined) return null;
  if (role !== "superadmin") return <Navigate to="/dashboard" />;

  const handleCalculate = async () => {
    setError(null);
    setResult(null);

    if (!selectedStoreId) {
      setError("Please select a Store Outlet.");
      return;
    }

    const subtotalVal = parseFloat(subtotal);
    const distanceVal = parseFloat(distance);

    if (isNaN(subtotalVal) || subtotalVal < 0) {
      setError("Please enter a valid non-negative subtotal.");
      return;
    }

    if (deliveryType === "DELIVERY" && (isNaN(distanceVal) || distanceVal < 0)) {
      setError("Please enter a valid non-negative distance.");
      return;
    }

    const selectedStore = stores.find(s => (s.id || s._id) === selectedStoreId);
    const token = localStorage.getItem("token");

    try {
      setLoading(true);
      
      // Fetch delivery settings to obtain the source-of-truth maximum delivery radius
      let maxRadius = 5;
      let storeSettings = null;
      try {
        const settingsResp = await GetDeliverySettings(token, selectedStoreId);
        storeSettings = extractData(settingsResp);
        if (storeSettings) {
          maxRadius = Number(storeSettings.maximumDeliveryRadius || (storeSettings.slabs && storeSettings.slabs.length > 0 ? Math.max(...storeSettings.slabs.map(s => s.to)) : 5));
        } else if (selectedStore) {
          maxRadius = Number(selectedStore.deliveryRadius || 5);
        }
      } catch (e) {
        console.warn("Failed to fetch store settings, falling back to store profile radius:", e);
        if (selectedStore) {
          maxRadius = Number(selectedStore.deliveryRadius || 5);
        }
      }

      // ── Stage 1: Store Validation ──────────────────
      const isActive = selectedStore ? selectedStore.status === "active" : true;
      const isOpen = selectedStore ? isStoreOpen(selectedStore.openingTime, selectedStore.closingTime) : true;
      const isWithinRadius = deliveryType !== "DELIVERY" || distanceVal <= maxRadius;

      if (!isActive) {
        setResult({
          deliveryCharge: "Not Calculated",
          freeDelivery: false,
          ruleMatched: "BR-L001",
          estimatedTime: "N/A",
          executionTimeMs: 0,
          isError: true,
          stage1Failed: true,
          reasonCode: "STORE_INACTIVE",
          errorReason: "This store cannot deliver to this address because it is currently INACTIVE.",
          storeRadius: maxRadius,
          customerDistance: distanceVal,
          storeActive: false,
          storeOpen: isOpen,
          withinRadius: isWithinRadius
        });
        return;
      }

      if (!isOpen) {
        setResult({
          deliveryCharge: "Not Calculated",
          freeDelivery: false,
          ruleMatched: "BR-L002",
          estimatedTime: "N/A",
          executionTimeMs: 0,
          isError: true,
          stage1Failed: true,
          reasonCode: "STORE_CLOSED",
          errorReason: `This store cannot deliver to this address because it is currently CLOSED. Operating hours: ${selectedStore?.openingTime || '—'} to ${selectedStore?.closingTime || '—'}.`,
          storeRadius: maxRadius,
          customerDistance: distanceVal,
          storeActive: isActive,
          storeOpen: false,
          withinRadius: isWithinRadius
        });
        return;
      }

      if (!isWithinRadius) {
        setResult({
          deliveryCharge: "Not Calculated",
          freeDelivery: false,
          ruleMatched: "BR-L003",
          estimatedTime: "N/A",
          executionTimeMs: 0,
          isError: true,
          stage1Failed: true,
          reasonCode: "OUTSIDE_RADIUS",
          errorReason: `Customer location is ${distanceVal.toFixed(1)} KM away. Store delivery radius is ${maxRadius.toFixed(1)} KM.`,
          storeRadius: maxRadius,
          customerDistance: distanceVal,
          storeActive: isActive,
          storeOpen: isOpen,
          withinRadius: false
        });
        return;
      }

      // ── Stage 2 & 3: Calculation & Estimated Time ──
      const payload = {
        storeId: selectedStoreId,
        distance: deliveryType === "DELIVERY" ? distanceVal : 0,
        cartSubtotal: subtotalVal,
        deliveryType
      };

      const response = await CalculateDeliveryPlayground(token, payload);
      const resData = extractData(response);

      setResult({
        ...resData,
        isError: false,
        stage1Failed: false,
        storeRadius: maxRadius,
        customerDistance: distanceVal,
        storeActive: isActive,
        storeOpen: isOpen,
        withinRadius: isWithinRadius
      });
    } catch (err) {
      console.error("[DeliveryPlayground] Error:", err);
      const errMsg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message;
      const latency = err?.response?.data?.error?.executionTimeMs || 0;

      if (errMsg === "OUTSIDE_DELIVERY_RADIUS") {
        setResult({
          deliveryCharge: "Not Calculated",
          freeDelivery: false,
          ruleMatched: "BR-L003",
          estimatedTime: "N/A",
          executionTimeMs: latency,
          isError: true,
          stage1Failed: true,
          reasonCode: "OUTSIDE_RADIUS",
          errorReason: `Customer location is outside the store's delivery slabs/radius limits.`,
          storeRadius: selectedStore ? Number(selectedStore.deliveryRadius || 5) : 5,
          customerDistance: distanceVal,
          storeActive: true,
          storeOpen: true,
          withinRadius: false
        });
      } else if (errMsg === "DELIVERY_SETTINGS_NOT_CONFIGURED") {
        setResult({
          deliveryCharge: "Not Calculated",
          freeDelivery: false,
          ruleMatched: "SETTINGS_MISSING",
          estimatedTime: "N/A",
          executionTimeMs: latency,
          isError: true,
          stage1Failed: true,
          reasonCode: "SETTINGS_MISSING",
          errorReason: "Delivery settings configuration not found for this store outlet.",
          storeRadius: selectedStore ? Number(selectedStore.deliveryRadius || 5) : 5,
          customerDistance: distanceVal,
          storeActive: true,
          storeOpen: true,
          withinRadius: true
        });
      } else {
        setError(errMsg || "Unknown error during delivery charge calculation.");
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedStoreName = stores.find(s => (s.id || s._id) === selectedStoreId)?.name || "Store";

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Developer Tools"
          breadcrumbItem="Delivery Playground"
        />

        <Row>
          {/* ── Configuration Inputs ──────────────────────── */}
          <Col lg={4}>
            <Card className="shadow-sm border-0">
              <CardBody>
                <div className="d-flex align-items-center mb-4 gap-2">
                  <i
                    className="mdi mdi-calculator"
                    style={{ fontSize: "1.5rem", color: "#556ee6" }}
                  />
                  <h5 className="mb-0 fw-semibold">Diagnostics Input</h5>
                </div>

                {loadingStores ? (
                  <div className="text-center py-4">
                    <Spinner size="sm" color="primary" />
                    <p className="mt-2 text-muted small">Loading stores...</p>
                  </div>
                ) : (
                  <>
                    <FormGroup className="mb-3">
                      <Label for="storeSelect" className="fw-semibold">Store Outlet</Label>
                      <Input
                        type="select"
                        id="storeSelect"
                        value={selectedStoreId}
                        onChange={(e) => setSelectedStoreId(e.target.value)}
                      >
                        <option value="">-- Select Outlet --</option>
                        {stores.map((s) => (
                          <option key={s.id || s._id} value={s.id || s._id}>
                            {s.name}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>

                    <FormGroup className="mb-3">
                      <Label for="deliveryTypeSelect" className="fw-semibold">Delivery Type</Label>
                      <Input
                        type="select"
                        id="deliveryTypeSelect"
                        value={deliveryType}
                        onChange={(e) => setDeliveryType(e.target.value)}
                      >
                        <option value="DELIVERY">DELIVERY (Standard Delivery)</option>
                        <option value="PICKUP">PICKUP (Store Pickup)</option>
                        <option value="DINE_IN">DINE_IN (Dine In)</option>
                      </Input>
                    </FormGroup>

                    {deliveryType === "DELIVERY" && (
                      <FormGroup className="mb-3">
                        <Label for="distanceInput" className="fw-semibold">Distance (KM)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          id="distanceInput"
                          placeholder="e.g. 2.4"
                          value={distance}
                          onChange={(e) => setDistance(e.target.value)}
                        />
                      </FormGroup>
                    )}

                    <FormGroup className="mb-4">
                      <Label for="subtotalInput" className="fw-semibold">Cart Subtotal (₹)</Label>
                      <Input
                        type="number"
                        min="0"
                        id="subtotalInput"
                        placeholder="e.g. 250"
                        value={subtotal}
                        onChange={(e) => setSubtotal(e.target.value)}
                      />
                    </FormGroup>

                    <Button
                      color="primary"
                      className="w-100 fw-semibold"
                      onClick={handleCalculate}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Calculating...
                        </>
                      ) : (
                        "Run Diagnostics"
                      )}
                    </Button>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* ── Diagnostics Output ─────────────────────── */}
          <Col lg={8}>
            {error && (
              <Alert color="danger" className="border-0 shadow-sm mb-4">
                <i className="mdi mdi-alert-circle-outline me-2" />
                {error}
              </Alert>
            )}

            {result ? (
              <div className="fade-in">
                {/* Metrics Row */}
                <Row className="mb-4">
                  <Col sm={6} md={3} className="mb-3">
                    <Card className="mini-stats-wid shadow-sm border-0 h-100">
                      <CardBody className="d-flex flex-column justify-content-between">
                        <div className="d-flex">
                          <div className="flex-grow-1">
                            <p className="text-muted fw-medium mb-2 small">Delivery Charge</p>
                            <h4 className={`mb-0 fw-bold ${result.isError ? "text-danger" : ""}`}>
                              {result.isError ? result.deliveryCharge : `₹ ${result.deliveryCharge}`}
                            </h4>
                          </div>
                          <div className="mini-stat-icon avatar-sm rounded-circle bg-light align-self-center">
                            <span className={`avatar-title bg-soft rounded-circle ${result.isError ? "bg-soft-danger text-danger" : "bg-soft-success text-success"}`}>
                              <i className="mdi mdi-currency-inr" style={{ fontSize: "1.2rem" }} />
                            </span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col sm={6} md={3} className="mb-3">
                    <Card className="mini-stats-wid shadow-sm border-0 h-100">
                      <CardBody className="d-flex flex-column justify-content-between">
                        <div className="d-flex">
                          <div className="flex-grow-1">
                            <p className="text-muted fw-medium mb-2 small">Rule Matched</p>
                            <h5 className={`mb-0 fw-bold ${result.isError ? "text-danger" : "text-primary"}`}>{result.ruleMatched || "—"}</h5>
                          </div>
                          <div className="mini-stat-icon avatar-sm rounded-circle bg-light align-self-center">
                            <span className={`avatar-title bg-soft rounded-circle ${result.isError ? "bg-soft-danger text-danger" : "bg-soft-primary text-primary"}`}>
                              <i className="mdi mdi-file-document-outline" style={{ fontSize: "1.2rem" }} />
                            </span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col sm={6} md={3} className="mb-3">
                    <Card className="mini-stats-wid shadow-sm border-0 h-100">
                      <CardBody className="d-flex flex-column justify-content-between">
                        <div className="d-flex">
                          <div className="flex-grow-1">
                            <p className="text-muted fw-medium mb-2 small">Estimated Time</p>
                            <h5 className="mb-0 fw-bold text-dark">{result.estimatedTime || "—"}</h5>
                          </div>
                          <div className="mini-stat-icon avatar-sm rounded-circle bg-light align-self-center">
                            <span className="avatar-title bg-soft bg-info text-info rounded-circle">
                              <i className="mdi mdi-clock-outline" style={{ fontSize: "1.2rem" }} />
                            </span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col sm={6} md={3} className="mb-3">
                    <Card className="mini-stats-wid shadow-sm border-0 h-100">
                      <CardBody className="d-flex flex-column justify-content-between">
                        <div className="d-flex">
                          <div className="flex-grow-1">
                            <p className="text-muted fw-medium mb-2 small">Execution Time</p>
                            <h5 className="mb-0 fw-bold text-warning">{result.executionTimeMs} ms</h5>
                          </div>
                          <div className="mini-stat-icon avatar-sm rounded-circle bg-light align-self-center">
                            <span className="avatar-title bg-soft bg-warning text-warning rounded-circle">
                              <i className="mdi mdi-speedometer" style={{ fontSize: "1.2rem" }} />
                            </span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Detailed Logs & Slabs */}
                <Card className="shadow-sm border-0 mb-4">
                  <CardBody>
                    <h5 className="card-title mb-4">Execution Diagnostics Trace</h5>

                    {/* Diagnostics Validation Stages Checklist */}
                    <div className="mb-4 p-3 bg-light rounded">
                      <h6 className="fw-semibold mb-3 d-flex align-items-center gap-2 text-dark">
                        <i className="mdi mdi-shield-check text-primary" style={{ fontSize: "1.2rem" }} />
                        Diagnostics Validation Stages
                      </h6>
                      <div className="d-flex flex-column gap-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="small text-muted fw-semibold">Stage 1: Store Validation</span>
                          <div className="d-flex align-items-center gap-3">
                            <span className="small text-muted">
                              Active: {result.storeActive ? <span className="text-success fw-bold">✔</span> : <span className="text-danger fw-bold">❌</span>}
                            </span>
                            <span className="small text-muted">
                              Open: {result.storeOpen ? <span className="text-success fw-bold">✔</span> : <span className="text-danger fw-bold">❌</span>}
                            </span>
                            {deliveryType === "DELIVERY" && (
                              <span className="small text-muted">
                                Within Radius: {result.withinRadius ? <span className="text-success fw-bold">✔ ({Number(result.customerDistance).toFixed(1)} KM &lt;= {Number(result.storeRadius).toFixed(1)} KM)</span> : <span className="text-danger fw-bold">❌ ({Number(result.customerDistance).toFixed(1)} KM &gt; {Number(result.storeRadius).toFixed(1)} KM)</span>}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="border-top my-1"></div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="small text-muted fw-semibold">Stage 2: Delivery Charge</span>
                          <span className={result.stage1Failed ? "text-danger small fw-semibold" : "text-success small fw-semibold"}>
                            {result.stage1Failed ? "❌ Not Calculated (Stage 1 Failed)" : `✔ Calculated (₹ ${result.deliveryCharge})`}
                          </span>
                        </div>
                        <div className="border-top my-1"></div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="small text-muted fw-semibold">Stage 3: Estimated Time</span>
                          <span className={result.stage1Failed ? "text-danger small fw-semibold" : "text-info small fw-semibold"}>
                            {result.stage1Failed ? "❌ Not Calculated" : `✔ Resolved (${result.estimatedTime})`}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Distance Provider Diagnostics */}
                    {result.distanceDiagnostics && (
                      <div className="mb-4 p-3 bg-light rounded border-start border-4 border-info">
                        <h6 className="fw-semibold mb-3 d-flex align-items-center gap-2 text-dark">
                          <i className="mdi mdi-map-marker-distance text-info" style={{ fontSize: "1.2rem" }} />
                          Distance Service Diagnostics
                        </h6>
                        <Row>
                          <Col sm={6} className="mb-2">
                            <span className="small text-muted d-block">Distance Provider</span>
                            <span className="fw-bold text-dark">{result.distanceDiagnostics.provider}</span>
                          </Col>
                          <Col sm={6} className="mb-2">
                            <span className="small text-muted d-block">Straight-line Distance</span>
                            <span className="fw-bold text-dark">
                              {result.distanceDiagnostics.straightLineDistanceKm != null
                                ? `${Number(result.distanceDiagnostics.straightLineDistanceKm).toFixed(2)} KM`
                                : "—"}
                            </span>
                          </Col>
                          <Col sm={6} className="mb-2">
                            <span className="small text-muted d-block">Estimated Road Distance</span>
                            <span className="text-muted small">
                              {result.distanceDiagnostics.roadDistanceKm != null
                                ? `${Number(result.distanceDiagnostics.roadDistanceKm).toFixed(2)} KM`
                                : "— (N/A)"}
                            </span>
                          </Col>
                          <Col sm={6} className="mb-2">
                            <span className="small text-muted d-block">Estimated Duration</span>
                            <span className="text-muted small">
                              {result.distanceDiagnostics.estimatedDurationMinutes != null
                                ? `${result.distanceDiagnostics.estimatedDurationMinutes} mins`
                                : "— (N/A)"}
                            </span>
                          </Col>
                          <Col sm={6} className="mb-2">
                            <span className="small text-muted d-block">Provider Latency</span>
                            <span className="text-dark fw-semibold">
                              {result.distanceDiagnostics.executionTimeMs != null
                                ? `${result.distanceDiagnostics.executionTimeMs} ms`
                                : "0 ms"}
                            </span>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {result.isError && (
                      <Alert color="danger" className="border-0 shadow-sm mb-4">
                        <div className="d-flex align-items-start gap-2">
                          <i className="mdi mdi-alert-circle-outline fs-5 mt-1" />
                          <div>
                            <h6 className="fw-bold mb-1 text-danger">Delivery Validation Failed</h6>
                            <p className="mb-1 small">
                              <strong>Reason:</strong> {result.errorReason}
                            </p>
                            <p className="mb-0 small">
                              <strong>Result:</strong> This store cannot deliver to this address.
                            </p>
                          </div>
                        </div>
                      </Alert>
                    )}
                    
                    <Table responsive borderless className="align-middle">
                      <tbody>
                        <tr>
                          <td style={{ width: "35%" }} className="text-muted small">Target Outlet</td>
                          <td className="fw-semibold">{selectedStoreName}</td>
                        </tr>
                        <tr>
                          <td className="text-muted small">Cart Subtotal</td>
                          <td className="fw-semibold">₹ {subtotal}</td>
                        </tr>
                        {deliveryType === "DELIVERY" && (
                          <>
                            <tr>
                              <td className="text-muted small">Customer Distance</td>
                              <td className="fw-semibold">{Number(distance).toFixed(1)} KM</td>
                            </tr>
                            <tr>
                              <td className="text-muted small">Store Delivery Radius</td>
                              <td className="fw-semibold">{Number(result.storeRadius).toFixed(1)} KM</td>
                            </tr>
                          </>
                        )}
                        <tr>
                          <td className="text-muted small">Free Delivery Qualifier</td>
                          <td>
                            {result.freeDelivery ? (
                              <Badge color="success" className="px-2 py-1">APPLIED (Subtotal Above Free Limit)</Badge>
                            ) : (
                              <Badge color="secondary" className="px-2 py-1">{result.stage1Failed ? "NOT APPLICABLE" : "NOT APPLIED"}</Badge>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted small">Resolved Rule</td>
                          <td>
                            <code className={result.isError ? "text-danger" : ""}>{result.ruleMatched}</code>
                            {result.ruleMatched === "BR-D001" && (
                              <span className="ms-2 text-success small">(Free Delivery for Subtotal &gt;= ₹300)</span>
                            )}
                            {result.ruleMatched === "BR-D002" && (
                              <span className="ms-2 text-success small">(Distance &lt;= 1 KM, charge ₹0)</span>
                            )}
                            {result.ruleMatched === "BR-D003" && (
                              <span className="ms-2 text-muted small">(Distance 1-2 KM, charge ₹20)</span>
                            )}
                            {result.ruleMatched === "BR-D004" && (
                              <span className="ms-2 text-muted small">(Distance 2-3 KM, charge ₹30)</span>
                            )}
                            {result.ruleMatched === "BR-D005" && (
                              <span className="ms-2 text-muted small">(Distance 3-5 KM, charge ₹40)</span>
                            )}
                            {result.ruleMatched === "BR-L001" && (
                              <span className="ms-2 text-danger small">(Store status is currently Inactive)</span>
                            )}
                            {result.ruleMatched === "BR-L002" && (
                              <span className="ms-2 text-danger small">(Store is closed during current hours)</span>
                            )}
                            {result.ruleMatched === "BR-L003" && (
                              <span className="ms-2 text-danger small">(Customer is outside store delivery radius)</span>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted small">Calculation Result</td>
                          <td className={`fw-bold fs-5 ${result.isError ? "text-danger" : "text-success"}`}>
                            {result.isError ? result.deliveryCharge : `₹ ${result.deliveryCharge}`}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              </div>
            ) : (
              <Card className="shadow-sm border-0 text-center py-5 border-dashed">
                <CardBody className="py-5">
                  <i
                    className="mdi mdi-google-play-billing text-muted mb-3"
                    style={{ fontSize: "3rem" }}
                  />
                  <h5 className="text-muted">Awaiting Diagnostics Trigger</h5>
                  <p className="text-muted small mx-auto" style={{ maxWidth: "450px" }}>
                    Configure the Store Outlet, Subtotal, Distance, and Delivery Type inputs on the left pane and click "Run Diagnostics" to verify delivery fee mapping rules.
                  </p>
                </CardBody>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
}
